// Template-specific mapping functions for template 69842241581723c403c697fc
// This template matches the backend pdf-maker template 630cebfecb9741ec6761744d

import {
  TEMPLATE_ID_DUAL_SHARE_CLASS,
  TEMPLATE_ID_LONG_TERM_VALUE_FUND,
  TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE,
  DEFAULT_DISCLAIMER_TEXT,
  DEFAULT_FUSSNOTE_TEXT,
  templateHasPieAdditionChart,
} from './templateConstants';
import { normalizePieTitlesForTemplate } from './pieChartDisplay';
import { applyDualSharePreiseSplit, splitMepreisInPreise } from './preiseFields';
import { resolveTitleAddition } from './titleAddition';
import { resolveAnlageberater } from './anlageberater';
import { normalizeRollingPerformance } from './rollingPerformance';
import {
  clampMonthlyCommentaryColumn,
  getMonthlyCommentaryMaxChars,
  MONTHLY_COMMENTARY_MAX_CHARS_PER_COLUMN,
} from './monthlyCommentaryImage';

export { splitMepreisInPreise } from './preiseFields';
export {
  getMonthlyCommentaryMaxChars,
  getMonthlyCommentaryMaxCharsPerColumn,
  MONTHLY_COMMENTARY_MAX_CHARS_PER_COLUMN,
} from './monthlyCommentaryImage';

/** Default Stammdaten — Vertriebszulassung (shown when field not yet stored) */
export const DEFAULT_VERTREIBSZULASSUNG = 'Deutschland, Luxemburg, Österreich, Schweiz';

/** Fourth pie chart default for dual share-class template (MARKTKAPITALISIERUNG AKTIENANTEIL) */
export const DEFAULT_PIE_ADDITION_DUAL_SHARE_CLASS = [
  { label: 'Mid Cap (1-10 Mrd. €)', value: 43.82 },
  { label: 'Large Cap (>10 Mrd. €)', value: 38.83 },
  { label: 'Small Cap (1 Mrd. €)', value: 17.35 },
];

const DEFAULT_PIE_TITLES_THREE_PIE_RENAME = [
  'TOP-5-AKTIEN (GESAMTPORTFOLIO)',
  'VERMÖGENSVERTEILUNG',
  'BRANCHEN (AKTIENANTEIL)',
  'REGIONEN (AKTIENANTEIL)',
];

const DEFAULT_PIE_TITLES_DUAL_SHARE_CLASS = [
  ...DEFAULT_PIE_TITLES_THREE_PIE_RENAME,
  'MARKTKAPITALISIERUNG (AKTIENANTEIL)',
];

/** Display order: VERMÖGENSVERTEILUNG, BRANCHEN (AKTIENANTEIL), REGIONEN (AKTIENANTEIL) */
const DEFAULT_PIE_TITLES_LONG_TERM_VALUE_FUND = [
  'TOP-5-AKTIEN (GESAMTPORTFOLIO)',
  'VERMÖGENSVERTEILUNG',
  'BRANCHEN (AKTIENANTEIL)',
  'REGIONEN (AKTIENANTEIL)',
  'MARKTKAPITALISIERUNG (AKTIENANTEIL)',
];

const DEFAULT_PIE_TITLES_OMIT_ETHIK_WITH_FOURTH = [
  ...DEFAULT_PIE_TITLES_THREE_PIE_RENAME,
  'MARKTKAPITALISIERUNG (AKTIENANTEIL)',
];

/** Two share-class columns for template `TEMPLATE_ID_DUAL_SHARE_CLASS` (stored in additional_fields). */
export type ShareClassColumn = {
  label: string;
  isin: string;
  preise: Record<string, unknown>;
  konditionen: Record<string, unknown>;
  stammdaten: Record<string, unknown>;
};

export type ShareClassVisibilityMode = 'both' | 'column0' | 'column1';

/** Normalize monthly commentary: other templates use a string; this template uses { left, right }. */
function sanitizeCommentaryText(text: string): string {
  return text
    .replace(/\u00AD/g, '') // soft hyphen
    .replace(/\u2011/g, '-') // non-breaking hyphen
    .replace(/\u00A0/g, ' ') // nbsp
    .replace(/\u200B/g, '') // zero-width space
    .replace(/\u200C/g, '') // zero-width non-joiner
    .replace(/\u200D/g, '') // zero-width joiner
    .replace(/\uFEFF/g, '') // BOM
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Keep intentional Enter line breaks; only trim outer whitespace
    .replace(/^\n+/, '')
    .replace(/\n+$/, '')
    .replace(/^ +/gm, '')
    .replace(/ +$/gm, '');
}

/**
 * Split commentary on Enter for react-pdf.
 * A raw `\n` inside one <Text> ignores lineHeight (lines look glued);
 * callers should render each entry as its own <Text>.
 */
export function splitMonthlyCommentaryLines(text: string): string[] {
  if (!text) return [];
  return text.split('\n');
}

export function normalizeMonthlyCommentary(
  raw: unknown,
  templateId?: string,
): { left: string; right: string } {
  const maxLeft = getMonthlyCommentaryMaxChars(templateId, 'left');
  const maxRight = getMonthlyCommentaryMaxChars(templateId, 'right');
  if (raw == null || raw === '') return { left: '', right: '' };
  if (typeof raw === 'string') {
    return { left: clampMonthlyCommentaryColumn(sanitizeCommentaryText(raw), maxLeft), right: '' };
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    return {
      left: o.left != null ? clampMonthlyCommentaryColumn(sanitizeCommentaryText(String(o.left)), maxLeft) : '',
      right: o.right != null ? clampMonthlyCommentaryColumn(sanitizeCommentaryText(String(o.right)), maxRight) : '',
    };
  }
  return { left: '', right: '' };
}

export function mapV1FieldsToV2Data(fields: any, templateId?: string) {
  const additional = fields?.additional_fields || {};
  const rawStammdaten = fields?.stammdaten || {};
  const stammdaten = {
    ...rawStammdaten,
    anlageberater: resolveAnlageberater(
      'anlageberater' in rawStammdaten ? rawStammdaten.anlageberater : undefined,
      templateId,
    ),
    vertriebszulassung:
      'vertriebszulassung' in rawStammdaten
        ? String(rawStammdaten.vertriebszulassung ?? '')
        : DEFAULT_VERTREIBSZULASSUNG,
  };
  const templateIsin =
    additional?.historyIsin ||
    (typeof stammdaten.isin === 'string' ? stammdaten.isin.split('###')[0].trim() : undefined);
  const normalizedRollingPerformance = normalizeRollingPerformance(
    additional?.rollingPerformance,
    templateIsin,
  );
  const v2: any = {
    title: fields?.title || '',
    published_at: fields?.published_at || fields?.wertentwicklung_seit_auflage_end_date || new Date().toISOString(),
    intro: fields?.intro || [],
    investmentphilosophie: fields?.investmentphilosophie || '',
    preise: fields?.preise || {},
    konditionen: fields?.konditionen || {},
    stammdaten,
    wertentwicklung_seit_auflage: fields?.wertentwicklung_seit_auflage || [],
    wertentwicklung_seit_auflage_label: fields?.wertentwicklung_seit_auflage_label || 'Frankfurter Modern Value Index',
    wertentwicklung_seit_auflage_labels: fields?.wertentwicklung_seit_auflage_labels || [],
    wertentwicklung_nach_jahren: fields?.wertentwicklung_nach_jahren || [],
    wertentwicklung_nach_jahren_label: fields?.wertentwicklung_nach_jahren_label || 'Frankfurter Modern Value Index',
    wertentwicklung_nach_jahren_labels: fields?.wertentwicklung_nach_jahren_labels || [],
    wertentwicklung_nach_perioden: additional?.wertentwicklung_nach_perioden || fields?.wertentwicklung_nach_perioden || {},
    risikoindikatoren: additional?.risikoindikatoren || fields?.risikoindikatoren || null,
    subheaderLabels: fields?.subheaderLabels || [],
    strategie: fields?.strategie || '',
    disclaimer: fields?.disclaimer?.trim() ? fields.disclaimer : DEFAULT_DISCLAIMER_TEXT,
    fussnote: fields?.fussnote?.trim() ? fields.fussnote : DEFAULT_FUSSNOTE_TEXT,
    chancen: fields?.chancen || '',
    risiken: fields?.risiken || '',
    ethik: fields?.ethik || '',
    gesamtportfolio: fields?.gesamtportfolio || [],
    branchen: fields?.branchen || [],
    regionen: fields?.regionen || [],
    marktkapitalisierung: fields?.marktkapitalisierung || [],
    pie_addition:
      fields?.pie_addition?.length
        ? fields.pie_addition
        : templateHasPieAdditionChart(templateId)
          ? DEFAULT_PIE_ADDITION_DUAL_SHARE_CLASS
          : null,
    pie_titles: (() => {
      const defaults =
        templateId === TEMPLATE_ID_DUAL_SHARE_CLASS
          ? DEFAULT_PIE_TITLES_DUAL_SHARE_CLASS
          : templateId === TEMPLATE_ID_LONG_TERM_VALUE_FUND
            ? DEFAULT_PIE_TITLES_LONG_TERM_VALUE_FUND
            : templateId === TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE
              ? DEFAULT_PIE_TITLES_OMIT_ETHIK_WITH_FOURTH
              : [];
      const raw = fields?.pie_titles?.length ? fields.pie_titles : defaults;
      return (
        normalizePieTitlesForTemplate(raw, templateId, defaults) ?? raw
      );
    })(),
    title_addition: '',
    intro_third: fields?.intro_third || [],
    business_table: fields?.business_table || [],
    esg_valuation_date: fields?.esg_valuation_date || '',
    esg_coverage_percent: fields?.esg_coverage_percent ?? null,
    esg_exposure_flags: fields?.esg_exposure_flags || [],
    table_intro: fields?.table_intro || [],
    chart_intro: fields?.chart_intro || [],
    violation: fields?.violation || '',
    pollution: fields?.pollution || '',
    logos: fields?.logos || [],
    contact_fields: fields?.contact_fields || [],
    social_sites: fields?.social_sites || [],
    manImageUrl: additional?.manImageUrl || fields?.manImageUrl || null,
    headings: fields?.headings || {},
    // Chart config — stored in additional_fields (same pattern as other templates)
    historyIsin: additional?.historyIsin,
    historyUntil: additional?.historyUntil || fields?.wertentwicklung_seit_auflage_end_date,
    data_as_of: additional?.data_as_of,
    historyLegendLabel: additional?.historyLegendLabel || fields?.wertentwicklung_seit_auflage_label || 'Frankfurter Modern Value Index',
    historyLegendSubLabel: additional?.historyLegendSubLabel || fields?.wertentwicklung_seit_auflage_labels?.[0]?.subLabel || '',
    historyCurrencySymbol: additional?.historyCurrencySymbol || '€',
    // Bar chart config (rollingPerformance) — stored in additional_fields
    rollingPerformance: normalizedRollingPerformance,
    // Section headers (editable names for each section)
    sectionHeaders: additional?.sectionHeaders || {},
    // Monthly commentary (left/right split; coerce legacy string from additional_fields)
    monthlyCommentary: normalizeMonthlyCommentary(additional?.monthlyCommentary, templateId),
    // Dual share-class PREISE/KONDITIONEN/STAMMDATEN (template 6995ec804c4ced0db3932d1b)
    shareClassColumns: Array.isArray(additional?.shareClassColumns)
      ? additional.shareClassColumns.map((col: ShareClassColumn) => ({
          ...col,
          preise: applyDualSharePreiseSplit(col.preise as Record<string, unknown>, templateId),
          stammdaten: {
            ...(col.stammdaten || {}),
            anlageberater: resolveAnlageberater(
              col.stammdaten && 'anlageberater' in col.stammdaten
                ? col.stammdaten.anlageberater
                : undefined,
              templateId,
            ),
          },
        }))
      : undefined,
    shareClassVisibility: additional?.shareClassVisibility || undefined,
    shareClassVisibilityMode: additional?.shareClassVisibilityMode || undefined,
  };

  if (templateId === TEMPLATE_ID_DUAL_SHARE_CLASS) {
    v2.preise = applyDualSharePreiseSplit(v2.preise, templateId);
  }

  v2.title_addition = resolveTitleAddition(
    templateId,
    fields?.title_addition,
    v2.shareClassColumns,
  );

  return v2;
}

function normalizePieChartRows(rows: any[] | undefined) {
  if (!Array.isArray(rows)) return rows;
  return rows.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const value = row.value;
    if (value === '' || value == null) return row;
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? { ...row, value: asNumber } : row;
  });
}

export function buildCustomizePayload(v2Data: any, original: any, templateId?: string) {
  const payload: Array<{ key: string; value: any }> = [];
  const safeOriginal = original || {};
  const origAdditional = safeOriginal.additional_fields || {};

  if ((safeOriginal.title || '') !== (v2Data?.title || '')) {
    payload.push({ key: 'title', value: v2Data?.title || '' });
  }

  if ((safeOriginal.title_addition ?? '') !== (v2Data?.title_addition ?? '')) {
    payload.push({ key: 'title_addition', value: v2Data?.title_addition ?? '' });
  }

  const nextPublished = v2Data?.published_at;
  if (nextPublished && safeOriginal.published_at !== nextPublished) {
    payload.push({ key: 'published_at', value: nextPublished });
  }

  // Map all V1 core fields (defined in PdfDocumentField schema)
  if (v2Data?.intro && !deepEqual(safeOriginal.intro, v2Data.intro)) {
    payload.push({ key: 'intro', value: v2Data.intro });
  }
  if (v2Data?.investmentphilosophie && safeOriginal.investmentphilosophie !== v2Data.investmentphilosophie) {
    payload.push({ key: 'investmentphilosophie', value: v2Data.investmentphilosophie });
  }
  if (v2Data?.preise && !deepEqual(safeOriginal.preise, v2Data.preise)) {
    payload.push({ key: 'preise', value: v2Data.preise });
  }
  if (v2Data?.konditionen && !deepEqual(safeOriginal.konditionen, v2Data.konditionen)) {
    payload.push({ key: 'konditionen', value: v2Data.konditionen });
  }
  if (v2Data?.stammdaten && !deepEqual(safeOriginal.stammdaten, v2Data.stammdaten)) {
    payload.push({ key: 'stammdaten', value: v2Data.stammdaten });
  }
  if (v2Data?.strategie && safeOriginal.strategie !== v2Data.strategie) {
    payload.push({ key: 'strategie', value: v2Data.strategie });
  }
  if ((safeOriginal.disclaimer ?? '') !== (v2Data?.disclaimer ?? '')) {
    payload.push({ key: 'disclaimer', value: v2Data?.disclaimer ?? '' });
  }
  if ((safeOriginal.fussnote ?? '') !== (v2Data?.fussnote ?? '')) {
    payload.push({ key: 'fussnote', value: v2Data?.fussnote ?? '' });
  }
  if (v2Data?.chancen && safeOriginal.chancen !== v2Data.chancen) {
    payload.push({ key: 'chancen', value: v2Data.chancen });
  }
  if (v2Data?.risiken && safeOriginal.risiken !== v2Data.risiken) {
    payload.push({ key: 'risiken', value: v2Data.risiken });
  }
  if (v2Data?.ethik && safeOriginal.ethik !== v2Data.ethik) {
    payload.push({ key: 'ethik', value: v2Data.ethik });
  }
  if (v2Data?.gesamtportfolio && !deepEqual(safeOriginal.gesamtportfolio, v2Data.gesamtportfolio)) {
    payload.push({ key: 'gesamtportfolio', value: normalizePieChartRows(v2Data.gesamtportfolio) });
  }
  if (v2Data?.branchen && !deepEqual(safeOriginal.branchen, v2Data.branchen)) {
    payload.push({ key: 'branchen', value: normalizePieChartRows(v2Data.branchen) });
  }
  if (v2Data?.regionen && !deepEqual(safeOriginal.regionen, v2Data.regionen)) {
    payload.push({ key: 'regionen', value: normalizePieChartRows(v2Data.regionen) });
  }
  if (v2Data?.marktkapitalisierung && !deepEqual(safeOriginal.marktkapitalisierung, v2Data.marktkapitalisierung)) {
    payload.push({ key: 'marktkapitalisierung', value: normalizePieChartRows(v2Data.marktkapitalisierung) });
  }
  if (v2Data?.pie_addition != null && !deepEqual(safeOriginal.pie_addition, v2Data.pie_addition)) {
    payload.push({ key: 'pie_addition', value: normalizePieChartRows(v2Data.pie_addition) });
  }
  // risikoindikatoren saved via additional_fields (same pattern as performance table)
  // Keep backward compat by also writing to the schema field
  if (v2Data?.risikoindikatoren && !deepEqual(safeOriginal.risikoindikatoren, v2Data.risikoindikatoren)) {
    payload.push({ key: 'risikoindikatoren', value: v2Data.risikoindikatoren });
  }

  // Business table (defined in schema)
  if (v2Data?.business_table && !deepEqual(safeOriginal.business_table, v2Data.business_table)) {
    payload.push({ key: 'business_table', value: v2Data.business_table });
  }
  if (v2Data?.esg_valuation_date != null && safeOriginal.esg_valuation_date !== v2Data.esg_valuation_date) {
    payload.push({ key: 'esg_valuation_date', value: v2Data.esg_valuation_date });
  }
  if (v2Data?.esg_coverage_percent != null && safeOriginal.esg_coverage_percent !== v2Data.esg_coverage_percent) {
    payload.push({ key: 'esg_coverage_percent', value: v2Data.esg_coverage_percent });
  }
  if (v2Data?.esg_exposure_flags && !deepEqual(safeOriginal.esg_exposure_flags, v2Data.esg_exposure_flags)) {
    payload.push({ key: 'esg_exposure_flags', value: v2Data.esg_exposure_flags });
  }

  // Also update core V1 end-date field if changed to keep backend parity
  if (v2Data?.historyUntil && !deepEqual(safeOriginal.wertentwicklung_seit_auflage_end_date, v2Data?.historyUntil)) {
    payload.push({ key: 'wertentwicklung_seit_auflage_end_date', value: v2Data.historyUntil });
  }
  if (v2Data?.historyLegendLabel !== undefined && safeOriginal.wertentwicklung_seit_auflage_label !== v2Data.historyLegendLabel) {
    payload.push({ key: 'wertentwicklung_seit_auflage_label', value: v2Data.historyLegendLabel });
  }

  // --- Custom fields: store in additional_fields ---
  const updatedAdditional: any = {};

  // Chart config
  if (!deepEqual(origAdditional.historyIsin, v2Data?.historyIsin)) {
    updatedAdditional.historyIsin = v2Data?.historyIsin;
  }
  if (!deepEqual(origAdditional.historyUntil, v2Data?.historyUntil)) {
    updatedAdditional.historyUntil = v2Data?.historyUntil;
  }
  if (!deepEqual(origAdditional.historyLegendLabel, v2Data?.historyLegendLabel)) {
    updatedAdditional.historyLegendLabel = v2Data?.historyLegendLabel;
  }
  if (!deepEqual(origAdditional.historyLegendSubLabel, v2Data?.historyLegendSubLabel)) {
    updatedAdditional.historyLegendSubLabel = v2Data?.historyLegendSubLabel;
    // Keep wertentwicklung_seit_auflage_labels[0] in sync so the V1 PDF builder also picks it up
    const existingLabels: Array<{ label: string; subLabel?: string }> = Array.isArray(safeOriginal.wertentwicklung_seit_auflage_labels)
      ? [...safeOriginal.wertentwicklung_seit_auflage_labels]
      : [];
    const firstLabel = existingLabels[0]?.label || v2Data?.historyLegendLabel || '';
    const synced = [{ label: firstLabel, subLabel: v2Data?.historyLegendSubLabel || '' }, ...existingLabels.slice(1)];
    payload.push({ key: 'wertentwicklung_seit_auflage_labels', value: synced });
  }
  if (!deepEqual(origAdditional.historyCurrencySymbol, v2Data?.historyCurrencySymbol)) {
    updatedAdditional.historyCurrencySymbol = v2Data?.historyCurrencySymbol;
  }

  // Bar chart config (rollingPerformance)
  if (v2Data?.rollingPerformance && !deepEqual(origAdditional.rollingPerformance, v2Data.rollingPerformance)) {
    updatedAdditional.rollingPerformance = v2Data.rollingPerformance;
  }

  // Performance table (wertentwicklung_nach_perioden) — use additional_fields for new format
  if (v2Data?.wertentwicklung_nach_perioden && !deepEqual(origAdditional.wertentwicklung_nach_perioden, v2Data.wertentwicklung_nach_perioden)) {
    updatedAdditional.wertentwicklung_nach_perioden = v2Data.wertentwicklung_nach_perioden;
  }

  // Risk indicators table (risikoindikatoren) — also save in additional_fields for TableConfigurationDialog format
  if (v2Data?.risikoindikatoren && !deepEqual(origAdditional.risikoindikatoren, v2Data.risikoindikatoren)) {
    updatedAdditional.risikoindikatoren = v2Data.risikoindikatoren;
  }

  // Section headers (editable section names)
  if (v2Data?.sectionHeaders && !deepEqual(origAdditional.sectionHeaders, v2Data.sectionHeaders)) {
    updatedAdditional.sectionHeaders = v2Data.sectionHeaders;
  }

  // Monthly commentary (left/right split) — always persist canonical { left, right }
  const nextMonthly = normalizeMonthlyCommentary(v2Data?.monthlyCommentary, templateId);
  const origMonthly = normalizeMonthlyCommentary(origAdditional.monthlyCommentary, templateId);
  if (!deepEqual(origMonthly, nextMonthly)) {
    updatedAdditional.monthlyCommentary = nextMonthly;
  }

  // Page-1 Monatskommentar portrait (SHV + PVM editable override)
  if (v2Data?.manImageUrl !== undefined && !deepEqual(origAdditional.manImageUrl, v2Data.manImageUrl)) {
    updatedAdditional.manImageUrl = v2Data.manImageUrl;
  }

  if (v2Data?.shareClassColumns && !deepEqual(origAdditional.shareClassColumns, v2Data.shareClassColumns)) {
    updatedAdditional.shareClassColumns = v2Data.shareClassColumns;
  }
  if (v2Data?.shareClassVisibility && !deepEqual(origAdditional.shareClassVisibility, v2Data.shareClassVisibility)) {
    updatedAdditional.shareClassVisibility = v2Data.shareClassVisibility;
  }
  if (v2Data?.shareClassVisibilityMode && !deepEqual(origAdditional.shareClassVisibilityMode, v2Data.shareClassVisibilityMode)) {
    updatedAdditional.shareClassVisibilityMode = v2Data.shareClassVisibilityMode;
  }

  // Merge into additional_fields if any updates
  if (Object.keys(updatedAdditional).length) {
    const merged = { ...origAdditional, ...updatedAdditional };
    payload.push({ key: 'additional_fields', value: merged });
  }

  return payload;
}

function deepEqual(a: any, b: any) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
}
